import React, { useContext, useMemo } from 'react'
import { useHasReceptor } from 'src/pages/Receptors/receptors.hooks'
import { StandardOpenModalButton } from 'src/components/ModalForms/ModalButtons'
import { AddWorkflowEvidenceModal } from 'src/components/ModalForms/Evidence/AddWorkflowEvidence'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import {
  Control,
  CONTROL_STATUS,
} from '@trustero/trustero-api-web/lib/model/control_pb'
import { AuditRecord } from '@trustero/trustero-api-web/lib/audit/audit_pb'
import { Range } from 'react-date-range'
import { StandardButtonSize, StandardButtonVariant } from '../../Buttons'
import { ModalFormId } from '../../../ModalForms'
import { AddEvidenceModal } from '../../../ModalForms/Evidence/AddEvidence/AddEvidenceModal'
import { ReceptorScanSpinner } from '../../../../Throbber/ReceptorScanSpinner'
import { AddConnectionModalToggle } from '../../../../pages/Controls/ControlsShowPage/ConnectedGrid'
import ReceptorScanContext from '../../../../context/ReceptorScanStatus'
import { DateRangeFilter } from '../../TrusteroDateRange/DateRangeFilter'
import { FlexRow } from '../../Flex'
import {
  TabPanelButtonGroup,
  TabPanelNoteHeader,
  TabPanelNoteItem,
} from './styles'

export const EvidenceTabNote = ({
  control,
  currentAudit,
  setDateRange,
  dateRange,
}: {
  control: Control.AsObject
  currentAudit?: AuditRecord
  setDateRange: React.Dispatch<React.SetStateAction<Range>>
  dateRange: Range
}): JSX.Element => {
  const { receptorScanRunning } = useContext(ReceptorScanContext)

  const { data: HasJiraReceptor } = useHasReceptor('trr-jira-workflow', false)
  const hasJiraWorkflowReceptor = useMemo(
    () => HasJiraReceptor?.getHasReceptor() || false,
    [HasJiraReceptor],
  )

  const { data: HasSnowReceptor } = useHasReceptor(
    'trr-servicenow-workflow',
    false,
  )
  const hasServiceNowWorkflowReceptor = useMemo(
    () => HasSnowReceptor?.getHasReceptor() || false,
    [HasSnowReceptor],
  )

  const isNotApplicable = control.status === CONTROL_STATUS.NOTAPPLICABLE
  const controlId = control.id
  const name = control.name
  const canAddEvidence = useCanPerformAction<Control.AsObject>(
    CAPABILITY.CAN_ADD_EVIDENCE,
    control,
  )

  const addEvidenceModalId = isNotApplicable
    ? ModalFormId.NA_INFO
    : ModalFormId.ADD_EVIDENCE

  const addWorkflowEvidenceModalId = isNotApplicable
    ? ModalFormId.NA_INFO
    : ModalFormId.ADD_WORKFLOW_EVIDENCE

  const addConnectionModalId = isNotApplicable
    ? ModalFormId.NA_INFO
    : ModalFormId.ADD_EVIDENCE_GENERATOR_EVIDENCE_TAB

  return (
    <>
      <TabPanelNoteItem>
        <TabPanelNoteHeader>
          <DateRangeFilter
            currentAudit={currentAudit?.getId()}
            dateRange={dateRange}
            setDateRange={setDateRange}
            additionalToolTip={!!currentAudit}
            additionalToolTipBody="Auditors only consider evidence and tests from within the date range of your current audit."
          />
          <TabPanelButtonGroup>
            <FlexRow gap={12}>
              {(hasJiraWorkflowReceptor || hasServiceNowWorkflowReceptor) && (
                <StandardOpenModalButton
                  modalId={addWorkflowEvidenceModalId}
                  variant={StandardButtonVariant.SECONDARY}
                  text="Create Workflow Task"
                  size={StandardButtonSize.SMALL}
                />
              )}
              <AddConnectionModalToggle
                modalFormId={addConnectionModalId}
                buttonVariant={StandardButtonVariant.SECONDARY}
                control={control}
              />
              <StandardOpenModalButton
                modalId={addEvidenceModalId}
                text="Add Evidence"
                size={StandardButtonSize.SMALL}
                isDisabled={!canAddEvidence}
                disabledReason="You do not have permission to add evidence."
              />
            </FlexRow>
          </TabPanelButtonGroup>
        </TabPanelNoteHeader>
        <ReceptorScanSpinner show={receptorScanRunning} />
      </TabPanelNoteItem>
      <AddEvidenceModal controlId={controlId} name={name} openOnDrag />
      <AddWorkflowEvidenceModal controlId={controlId} name={name} openOnDrag />
    </>
  )
}
